/**
 * (c) 2026, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { forwardRef, useImperativeHandle, useState } from "react";
import { Box } from "@microbit/ui";
import { useAnimation } from "../AnimationProvider";

export interface ProgressBarRef {
  play(duration?: number): Promise<void>;
  reset(): void;
}

const ProgressBar = forwardRef<ProgressBarRef>(function ProgressBar(_, ref) {
  const { delayInSec, withPlayState } = useAnimation();
  const [durationInSecs, setDuration] = useState<null | number>(null);
  useImperativeHandle(
    ref,
    () => ({
      async play(secs = 1.5) {
        setDuration(secs);
        await delayInSec(secs);
      },
      reset() {
        setDuration(null);
      },
    }),
    [delayInSec]
  );
  if (durationInSecs === null) {
    return null;
  }
  return (
    <Box
      height="0.75em"
      width="5em"
      borderRadius="full"
      background="gray.200"
      position="relative"
    >
      <Box
        position="absolute"
        height="100%"
        borderRadius="full"
        zIndex={1}
        background="brand2.500"
        style={{
          animation: withPlayState(
            `trainingProgress ${durationInSecs}s ease-in-out forwards`
          ),
        }}
        bottom={0}
        display="block"
        left={0}
      />
    </Box>
  );
});

export default ProgressBar;
